// @greenlight-spec: 48-user-passthrough.md#test-sign-in
// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
/** `{redirect_uri}` in a hint is the install's connect callback. */
export const SIGN_IN_TEST_REDIRECT_TOKEN = '{redirect_uri}';
/** The last step of every per-user OAuth setup guide. */
export const TEST_SIGN_IN_STEP = {
    title: 'Test sign-in',
    body: 'After Register, press Test sign-in and sign in as yourself. It runs the same connect a person using an app runs, so it fails exactly where theirs would.\n' +
        'A pass shows the account it matched and leaves you connected; you can disconnect like anyone else. A failure names the cause and the fix. Test again after any change to the registration.',
};
export const ENTRA_SIGN_IN_TEST_HINTS = [
    {
        code: 'AADSTS500113',
        cause: 'The app registration has no Web redirect URI for Greenlight, so Microsoft has nowhere to send you back.',
        fix: `In Entra, open the registration → Authentication → Add Redirect URI → Web, and enter exactly ${SIGN_IN_TEST_REDIRECT_TOKEN}. A URI under Single-page application or Mobile and desktop does not count.`,
    },
    {
        code: 'AADSTS50011',
        cause: 'The redirect URI Greenlight sent is not one registered on the app.',
        fix: `Under Authentication → Web, register exactly ${SIGN_IN_TEST_REDIRECT_TOKEN}: same scheme, host, path, and no trailing slash.`,
    },
    {
        code: 'AADSTS700016',
        cause: 'Entra found no app with this client id in this tenant.',
        fix: "Compare the credential's client id and the integration's tenant id with the registration's Overview page: Application (client) ID and Directory (tenant) ID, the GUID, not the domain.",
    },
    {
        code: 'AADSTS65001',
        cause: 'The registration’s delegated permissions have not been consented.',
        fix: 'Open API permissions → Grant admin consent for your tenant, until every permission shows Granted. Without it each person sees a consent page, or cannot connect at all.',
    },
    {
        code: 'AADSTS50105',
        cause: 'The registration requires assignment and you are not assigned to it, so Entra refuses you before any consent.',
        fix: 'In Entra, open Enterprise applications → this app → Users and groups and assign the people (or a group) who will use it, or set Assignment required to No under Properties.',
    },
    {
        code: 'AADSTS7000215',
        cause: 'Microsoft refused the client secret when Greenlight redeemed the sign-in.',
        fix: "Create a new client secret under Certificates & secrets, copy its Value (not the Secret ID), and replace the credential's secret in Greenlight.",
    },
    {
        code: 'AADSTS7000222',
        cause: 'The client secret has expired.',
        fix: "Create a new client secret under Certificates & secrets and replace the credential's secret in Greenlight.",
    },
];
export const SNOWFLAKE_SIGN_IN_TEST_HINTS = [
    {
        code: null,
        cause: 'Snowflake showed an error instead of sending you back: the security integration is disabled, or its OAUTH_REDIRECT_URI is not exactly Greenlight’s callback.',
        fix: `ALTER SECURITY INTEGRATION <name> SET ENABLED = TRUE OAUTH_REDIRECT_URI = '${SIGN_IN_TEST_REDIRECT_TOKEN}';`,
    },
    {
        code: null,
        cause: 'Snowflake refused the role: your Snowflake user does not hold the credential’s role, or the role is in BLOCKED_ROLES_LIST.',
        fix: 'GRANT ROLE <role> TO USER <you>; and keep the role out of the integration’s BLOCKED_ROLES_LIST.',
    },
    {
        code: 'invalid_client',
        cause: 'Snowflake refused the client id or secret when Greenlight redeemed the sign-in.',
        fix: "Run SELECT SYSTEM$SHOW_OAUTH_CLIENT_SECRETS('<NAME>'); and copy OAUTH_CLIENT_ID to the credential and OAUTH_CLIENT_SECRET to its secret.",
    },
];
/** For a Custom per-user OAuth registration on any other server. */
export const OAUTH_SIGN_IN_TEST_HINTS = [
    {
        code: null,
        cause: 'The provider showed an error instead of sending you back: its OAuth client does not list Greenlight’s callback as a redirect URI, or the client id is wrong.',
        fix: `Register exactly ${SIGN_IN_TEST_REDIRECT_TOKEN} as a redirect URI on the OAuth client, and check the credential's client id.`,
    },
    {
        code: 'invalid_client',
        cause: 'The provider refused the client id or secret when Greenlight redeemed the sign-in.',
        fix: "Copy the client id and a current secret from the provider, and replace the credential's secret in Greenlight.",
    },
    {
        code: 'invalid_scope',
        cause: 'The provider refused a scope the integration requests.',
        fix: 'Allow every scope the integration lists on the OAuth client. A server other than Entra or Snowflake must allow openid and email.',
    },
    {
        code: 'unauthorized_client',
        cause: 'The OAuth client is not allowed to use the authorization code grant.',
        fix: 'Enable the authorization code grant (with refresh tokens) on the OAuth client.',
    },
];
/** The hint for a code the provider returned, matched exactly. */
export function signInTestHintFor(hints, code) {
    return code ? hints.find((h) => h.code === code) : undefined;
}
/**
 * The provider code a refusal names: an Entra `AADSTSnnnn` from its description
 * or numeric `error_codes`, otherwise the OAuth `error`.
 */
export function signInTestProviderCode(refusal) {
    const aadsts = refusal.errorDescription?.match(/\bAADSTS\d+\b/)?.[0];
    if (aadsts)
        return aadsts;
    const first = refusal.errorCodes?.[0];
    if (typeof first === 'number')
        return `AADSTS${first}`;
    return refusal.error ?? null;
}
//# sourceMappingURL=sign-in-test-hints.js.map